import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { AlertCircleIcon } from 'lucide-react'
import {
	Alert, AlertDescription,
	Checkbox,
	FormDialog,
	RadioCard,
} from '@rentflowdev/ui'
import { propertiesApi, useUpdateTaxRegimeMutation } from '@/api'
import { extractErrorMessage } from '@/lib/error-message'
import type { TaxRegime, UsnIncomeBand } from '@/types'
import { TAX_REGIMES, USN_INCOME_BANDS } from '@/types'

interface TaxRegimeDialogProps {
	onOpenChange:     (open: boolean) => void
	taxRegime:        TaxRegime
	usnIncomeBand?:   UsnIncomeBand | null
	vatExemptArt145?: boolean
	vatVoluntary22?:  boolean
}

/** NONE is the "not stated" state — shown as a label, never offered as a choice. */
const SELECTABLE = TAX_REGIMES.filter((r) => r !== 'NONE')

/**
 * Mounted by the caller only while open, so the fields start from the saved
 * values on every open. Resetting them from an effect instead is what the
 * compiler rules forbid, and it is the reason this takes no `open` prop.
 */
export function TaxRegimeDialog({
	onOpenChange, taxRegime, usnIncomeBand, vatExemptArt145, vatVoluntary22,
}: TaxRegimeDialogProps) {
	const { t } = useTranslation()
	const dispatch = useDispatch()
	const [updateTaxRegime, { isLoading }] = useUpdateTaxRegimeMutation()

	const [regime, setRegime]       = useState<TaxRegime>(taxRegime === 'NONE' ? 'USN' : taxRegime)
	const [band, setBand]           = useState<UsnIncomeBand | undefined>(usnIncomeBand ?? undefined)
	const [art145, setArt145]       = useState(vatExemptArt145 ?? false)
	const [voluntary, setVoluntary] = useState(vatVoluntary22 ?? false)
	const [error, setError]         = useState<string | null>(null)

	// Mirrors the server rule: USN resolves no rate without one of the two.
	const bandMissing = regime === 'USN' && !band && !voluntary

	async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault()
		if (bandMissing) {
			setError(t('settings.profile.taxation.bandRequired'))
			return
		}
		setError(null)
		try {
			await updateTaxRegime({
				taxRegime:       regime,
				usnIncomeBand:   regime === 'USN' && !voluntary ? band : undefined,
				vatExemptArt145: regime === 'OSNO' ? art145 : false,
				vatVoluntary22:  regime === 'USN'  ? voluntary : false,
			}).unwrap()
			// getVatRate lives in a different createApi slice, so no mutation tag
			// can reach its cache — without this the rent-terms form previews the
			// old regime's rate for the cache lifetime.
			dispatch(propertiesApi.util.invalidateTags(['VatRate']))
			onOpenChange(false)
		} catch (err) {
			setError(extractErrorMessage(err) ?? t('settings.profile.taxation.saveFailed'))
		}
	}

	return (
		<FormDialog
			open
			onOpenChange={onOpenChange}
			title={t('settings.profile.taxation.dialogTitle')}
			submitLabel={t('common.save')}
			cancelLabel={t('common.cancel')}
			isSubmitting={isLoading}
			onSubmit={handleSubmit}
		>
			<div className="flex flex-col gap-4">
				<p className="text-sm text-(--color-muted-foreground)">
					{t('settings.profile.taxation.dialogHint')}
				</p>

				<div className="flex flex-col gap-3">
					{SELECTABLE.map((r) => (
						<RadioCard
							key={r}
							title={t(`settings.profile.taxation.regimes.${r}`)}
							description={t(`settings.profile.taxation.regimeHints.${r}`)}
							selected={regime === r}
							onClick={() => { setRegime(r); setError(null) }}
							onKeyDown={(e) => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault()
									setRegime(r)
									setError(null)
								}
							}}
						/>
					))}
				</div>

				{regime === 'USN' && (
					<div className="flex flex-col gap-2 border-t border-(--color-border) pt-4">
						{/* Bands stop mattering once the standard rate is elected — the
						    heading goes with them, or it hangs over a lone checkbox. */}
						{!voluntary && (
							<p className="text-sm font-[500]">{t('settings.profile.taxation.incomeBand')}</p>
						)}
						{!voluntary && (
							<>
								<div className="flex flex-col gap-3">
									{USN_INCOME_BANDS.map((b) => (
										<RadioCard
											key={b}
											title={t(`settings.profile.taxation.bands.${b}`)}
											selected={band === b}
											onClick={() => { setBand(b); setError(null) }}
											onKeyDown={(e) => {
												if (e.key === 'Enter' || e.key === ' ') {
													e.preventDefault()
													setBand(b)
													setError(null)
												}
											}}
										/>
									))}
								</div>
								<p className="text-xs text-(--color-muted-foreground)">
									{t('settings.profile.taxation.bandHint')}
								</p>
							</>
						)}

						<label className="mt-2 flex items-start gap-2">
							<Checkbox
								checked={voluntary}
								onCheckedChange={(c) => { setVoluntary(c === true); setError(null) }}
							/>
							<span className="flex flex-col gap-0.5">
								<span className="text-sm">{t('settings.profile.taxation.voluntary22')}</span>
								<span className="text-xs text-(--color-muted-foreground)">
									{t('settings.profile.taxation.voluntary22Hint')}
								</span>
							</span>
						</label>
					</div>
				)}

				{regime === 'OSNO' && (
					<label className="flex items-center gap-2 border-t border-(--color-border) pt-4">
						<Checkbox checked={art145} onCheckedChange={(c) => setArt145(c === true)} />
						<span className="text-sm">{t('settings.profile.taxation.exemptArt145')}</span>
					</label>
				)}

				{error && (
					<Alert variant="destructive">
						<AlertCircleIcon className="size-4" />
						<AlertDescription>{error}</AlertDescription>
					</Alert>
				)}
			</div>
		</FormDialog>
	)
}
